/**
 * @fileoverview WGSL-шейдеры растровых проходов v10.2.0
 * @module MikhmelViewer/Cinematic10/WGSLShaders
 *
 * Порт всего шейдерного стека v9.1.0 (GLSL ES 3.00 → WGSL) плюс новые
 * проходы из v9.2.0: volumetric lighting, SSGI, motion blur, AO v2, SSS.
 *
 * Соглашения:
 *  • bind group 0, binding 0 — uniform-структура прохода
 *  • binding 1 — линейный сэмплер (clamp-to-edge)
 *  • binding 2 — точечный сэмплер (nearest, clamp-to-edge)
 *  • binding 3+ — текстуры в порядке, объявленном в PASS_BINDINGS
 *
 * Везде используется textureSampleLevel(..., 0.0) вместо textureSample:
 * выборка внутри циклов с break — неоднородный поток управления, где
 * автоматический выбор LOD запрещён спецификацией WGSL.
 *
 * Ось Y: вершинный шейдер выдаёт uv с перевёрнутым Y, поэтому uv (0,0) —
 * левый верхний угол исходного изображения (как после UNPACK_FLIP_Y в v9).
 */

// ═══════════════════════════════════════════════════════════════════
// 1. ОБЩИЙ ПРОЛОГ — подставляется в начало каждого шейдера
// ═══════════════════════════════════════════════════════════════════

export const WGSL_COMMON = /* wgsl */`
const PI: f32 = 3.14159265359;
const TAU: f32 = 6.28318530718;
const LUMA709: vec3f = vec3f(0.2126, 0.7152, 0.0722);
const LUMA601: vec3f = vec3f(0.299, 0.587, 0.114);
const GOLDEN_ANGLE: f32 = 2.39996323;

fn srgbToLinear(c: vec3f) -> vec3f {
  let lo = c / 12.92;
  let hi = pow(max(c + vec3f(0.055), vec3f(0.0)) / 1.055, vec3f(2.4));
  return select(lo, hi, c > vec3f(0.04045));
}

fn linearToSrgb(c: vec3f) -> vec3f {
  let m = max(c, vec3f(0.0));
  let lo = m * 12.92;
  let hi = 1.055 * pow(m, vec3f(1.0 / 2.4)) - vec3f(0.055);
  return select(lo, hi, m > vec3f(0.0031308));
}

fn luma(c: vec3f) -> f32 { return dot(c, LUMA709); }
fn luma601(c: vec3f) -> f32 { return dot(c, LUMA601); }

// ── Контраст вокруг подвижной оси. 10.19.17 ──────────────────────────────
// ПОВОД. До 10.19.17 контраст считался как mix(vec3f(0.5), col, k) — ось 0,5 в
// ЛИНЕЙНОМ пространстве. Средняя яркость объёмного рендера КТ головы там около
// 0,006, то есть в 80 раз ниже оси. При k = 1,4 получалось
// 0,5 + 1,4·(0,006 − 0,5) = −0,19 → отрицательные значения → max(col, 0) →
// чёрный. Замер на живой сборке: rgb [21,19,14] → [25,18,5]; синий канал,
// самый тёмный, умирал первым — отсюда «уход в красный», который был на экране.
//
// ЧТО ТЕПЕРЬ. Кривая степенная относительно оси: log(out/ось) = k·log(in/ось).
// Ноль остаётся нулём, отрицательных значений не бывает НИКОГДА, кривая
// монотонна — порядок яркостей сохраняется, а для медицинской картинки это
// обязательное свойство. Считаем по светимости и масштабируем цвет целиком,
// чтобы контраст не крутил оттенок. Ось — не константа, а средняя яркость
// кадра (uMidGray): контраст поворачивается вокруг СОБСТВЕННОЙ середины
// изображения, поэтому средняя яркость сохраняется, а не падает втрое.
fn contrastAboutPivot(c: vec3f, k: f32, pivotIn: f32) -> vec3f {
  if (abs(k - 1.0) < 0.001) { return c; }
  let pivot = clamp(pivotIn, 0.002, 0.5);
  let l = max(luma(c), 1e-6);
  let l2 = pivot * pow(l / pivot, max(k, 0.05));
  return c * (l2 / l);
}

fn hash21(p: vec2f) -> f32 {
  return fract(sin(dot(p, vec2f(127.1, 311.7))) * 43758.5453);
}

fn hash31(p: vec3f) -> f32 {
  return fract(sin(dot(p, vec3f(127.1, 311.7, 74.7))) * 43758.5453);
}

// Интерливинг-градиентный шум (Jimenez) — лучше распределён, чем sin-хеш,
// и стабилен во времени при подаче кадрового смещения.
fn ignNoise(pixel: vec2f, frame: f32) -> f32 {
  let p = pixel + 5.588238 * fract(frame * 0.6180339887);
  return fract(52.9829189 * fract(dot(p, vec2f(0.06711056, 0.00583715))));
}

fn acesToneMap(x: vec3f) -> vec3f {
  let a = 2.51; let b = 0.03; let c = 2.43; let d = 0.59; let e = 0.14;
  return clamp((x * (a * x + vec3f(b))) / (x * (c * x + vec3f(d)) + vec3f(e)), vec3f(0.0), vec3f(1.0));
}

fn reinhardToneMap(c: vec3f) -> vec3f { return c / (vec3f(1.0) + c); }

fn filmicU2(x: vec3f) -> vec3f {
  let A = 0.15; let B = 0.50; let C = 0.10; let D = 0.20; let E = 0.02; let F = 0.30;
  return ((x * (A * x + vec3f(C * B)) + vec3f(D * E)) / (x * (A * x + vec3f(B)) + vec3f(D * F))) - vec3f(E / F);
}

fn filmicToneMap(c: vec3f) -> vec3f {
  return filmicU2(c * 2.0) / filmicU2(vec3f(11.2));
}

// Фаза Хеньи–Гринштейна — угловое распределение рассеяния в среде.
// g > 0 — рассеяние вперёд (туман, дым), g < 0 — назад.
fn henyeyGreenstein(cosTheta: f32, g: f32) -> f32 {
  let g2 = g * g;
  let denom = 1.0 + g2 - 2.0 * g * cosTheta;
  return (1.0 - g2) / (4.0 * PI * pow(max(denom, 1e-4), 1.5));
}
`;

// ═══════════════════════════════════════════════════════════════════
// 2. ВЕРШИННЫЙ ШЕЙДЕР (общий для всех растровых проходов)
// ═══════════════════════════════════════════════════════════════════

export const WGSL_VERTEX = /* wgsl */`
struct VSOut {
  @builtin(position) position: vec4f,
  @location(0) uv: vec2f,
};

@vertex
fn vs_main(@builtin(vertex_index) vi: u32) -> VSOut {
  var corners = array<vec2f, 4>(
    vec2f(-1.0, -1.0), vec2f( 1.0, -1.0),
    vec2f(-1.0,  1.0), vec2f( 1.0,  1.0)
  );
  let xy = corners[vi];
  var out: VSOut;
  out.position = vec4f(xy, 0.0, 1.0);
  // Инверсия Y: uv(0,0) должен указывать на левый верхний угол источника
  out.uv = vec2f(xy.x, -xy.y) * 0.5 + vec2f(0.5);
  return out;
}
`;

// ═══════════════════════════════════════════════════════════════════
// 3. ХЕЛПЕР ГЛУБИНЫ — общий для всех проходов, читающих depth
// ═══════════════════════════════════════════════════════════════════

const DEPTH_HELPER = /* wgsl */`
// ПОВОД, 10.19.17. Здесь стояло: «без буфера глубины яркость служит грубым
// прокси». Это неправда, и она стоила вам чёрного кадра. Яркость — не глубина:
// на объёмном рендере светлая кость у ЗАДНЕЙ стенки черепа даёт «маленькую
// глубину», а тёмный воздух ПЕРЕД лицом — «большую». SSAO из такого прокси
// изобретает окклюзию там, где геометрии нет. Замер на живой сборке (окно
// 344×416, синтетическая КТ): включение одного SSAO давало
// заполненность 0,278 → 0,156 и яркость 15,7 → 4,4. Кадр темнел в 3,5 раза
// БЕЗ единого настоящего значения глубины.
//
// ЧТО ТЕПЕРЬ. Нет буфера глубины — возвращается КОНСТАНТА. Все проходы,
// читающие глубину, на константе дают нейтральный результат: разность глубин
// нулевая, значит окклюзии нет, отражений нет, вектора движения нулевые.
// Плюс к этому такие проходы не запускаются вообще (cinematicRendering.js,
// _renderFrame), а панель прямо говорит, что затенение недоступно. Отказ с
// названной причиной лучше правдоподобного числа.
const DEPTH_UNAVAILABLE: f32 = 0.5;

fn getDepth(uv: vec2f) -> f32 {
  if (U.uHasDepth != 0) {
    // 10.19.18: выборка глубины — sampNearest, а не sampLinear, и это не
    // придирка. Карта глубины приходит форматом r32float, а фильтрующая выборка
    // такого формата разрешена лишь при возможности устройства
    // «float32-filterable». Если её нет, весь bind group становится негодным —
    // а негодный проход в WebGPU роняет ВЕСЬ командный буфер, то есть кадр
    // пропадает целиком. Ровно такие «Invalid …» и были в журнале с RTX 4060.
    return textureSampleLevel(texDepth, sampNearest, uv, 0.0).r;
  }
  return DEPTH_UNAVAILABLE;
}

// Нормаль из производных глубины. Дёшево и достаточно для screen-space
// эффектов; при наличии настоящего G-buffer заменяется на чтение нормалей.
fn depthNormal(uv: vec2f, texel: vec2f) -> vec3f {
  let c = getDepth(uv);
  let dx = getDepth(uv + vec2f(texel.x, 0.0)) - c;
  let dy = getDepth(uv + vec2f(0.0, texel.y)) - c;
  return normalize(vec3f(-dx * U.uResolution.x, -dy * U.uResolution.y, 1.0));
}
`;

// ═══════════════════════════════════════════════════════════════════
// 3б. ХЕЛПЕР НОРМАЛЕЙ [10.19.21] — настоящие нормали из объёма
// ═══════════════════════════════════════════════════════════════════

const NORMAL_HELPER = /* wgsl */`
// Карта нормалей приходит из того же лучевого прохода, что и глубина
// (depthFromVolume.js): rgb — мировой вектор в кодировке n·0,5+0,5, альфа —
// годность (0 у промаха и у вырожденного градиента). Это НЕ depthNormal:
// та — экранная поделка из производных глубины с осью z, приколоченной к
// единице; эта — настоящий градиент непрозрачности объёма, переведённый в мир.
//
// Выборка НАРОЧНО ближайшая: усреднение нормалей фильтрацией поперёк силуэта
// дало бы вектор, не принадлежащий ни одной из двух поверхностей.
fn getNormal(uv: vec2f) -> vec4f {
  let t = textureSampleLevel(texNormal, sampNearest, uv, 0.0);
  let n = t.xyz * 2.0 - 1.0;
  let l = length(n);
  if (t.a < 0.5 || l < 0.1) { return vec4f(0.0, 0.0, 1.0, 0.0); }
  return vec4f(n / l, 1.0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 4. SSAO — базовый проход окклюзии
// ═══════════════════════════════════════════════════════════════════

export const FRAG_SSAO = /* wgsl */`
struct Params {
  uViewProj: mat4x4f,
  uInvViewProj: mat4x4f,
  uResolution: vec2f,
  uRadius: f32,
  uIntensity: f32,
  uBias: f32,
  uSamples: i32,
  uHasDepth: i32,
  uFrame: f32,
  // 10.19.18: окно окклюзии в ДОЛЯХ диапазона near…far. Глубина теперь
  // линейная (depthMath.js), поэтому у порога есть смысл: 0,02 — это два
  // процента расстояния от ближней до дальней плоскости.
  uDepthWindow: f32,
  // [10.19.21] Есть ли карта нормалей И матрицы камеры. Без любого из двух
  // полусферу строить не из чего — работает прежний дисковый путь.
  uHasNormals: i32,
  // [10.19.21] Радиус полусферы В МИЛЛИМЕТРАХ (то же значение, что окноММ):
  // «затеняем то, что ближе на N мм» остаётся правдой и здесь.
  uRadiusWorld: f32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var sampLinear: sampler;
@group(0) @binding(2) var sampNearest: sampler;
@group(0) @binding(3) var texScene: texture_2d<f32>;
@group(0) @binding(4) var texDepth: texture_2d<f32>;
@group(0) @binding(5) var texNormal: texture_2d<f32>;

${DEPTH_HELPER}
${NORMAL_HELPER}

// Восстановление мировой позиции из глубины — ТА ЖЕ формула, что в объёмном
// свете (worldFromDepth там). Для ПАРАЛЛЕЛЬНОЙ проекции — а объёмное окно
// Cornerstone3D параллельное — это ТОЧНО: z ортоматрицы линеен, и
// ndc.z·0,5+0,5 совпадает с нашей линейной глубиной один в один. Для
// перспективы это приближение; рабочий случай — параллельный.
fn worldFromDepth(uv: vec2f, depth: f32) -> vec3f {
  let ndc = vec4f(uv.x * 2.0 - 1.0, (1.0 - uv.y) * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
  let world = U.uInvViewProj * ndc;
  return world.xyz / max(world.w, 1e-6);
}

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let depth = getDepth(uv);
  let texel = vec2f(1.0) / U.uResolution;

  // Фон окклюзии не даёт. Раньше здесь стояло «depth < 0.02» — условие для
  // аппаратной глубины, где у пустоты ноль. У нашей карты пустота — ДАЛЬНЯЯ
  // плоскость, то есть 1.0, и старое условие не срабатывало никогда.
  if (U.uHasDepth == 0 || depth >= 0.999) { return vec4f(1.0); }

  var occlusion = 0.0;
  var count = 0.0;
  let jitter = ignNoise(uv * U.uResolution, U.uFrame);
  let maxSamples = min(U.uSamples, 32);

  let nv = getNormal(uv);
  if (U.uHasNormals != 0 && nv.w > 0.5) {
    // ── [10.19.21] ПОЛУСФЕРА ВОКРУГ НАСТОЯЩЕЙ НОРМАЛИ — вместо чистых
    // разностей глубины. Точки берутся в МИРЕ (миллиметрах) в полусфере над
    // поверхностью, проецируются на экран и сравниваются с картой глубины:
    // заслонена та точка, перед которой есть геометрия. Дисковый путь ниже
    // затенял и то, что лежит В ПЛОСКОСТИ поверхности (у него нет понятия
    // «над»), — отсюда серые скаты на любой наклонной кости.
    let N = nv.xyz;
    let P = worldFromDepth(uv, depth);

    for (var i = 0; i < 32; i++) {
      if (i >= maxSamples) { break; }
      let fi = f32(i);

      // Равномерные направления по сфере (спираль Фибоначчи), джиттер
      // вращает набор от кадра к кадру; отражение в полусферу нормали.
      let z = 1.0 - 2.0 * (fi + 0.5) / f32(maxSamples);
      let r = sqrt(max(0.0, 1.0 - z * z));
      let phi = fi * GOLDEN_ANGLE + jitter * TAU;
      var dir = vec3f(r * cos(phi), r * sin(phi), z);
      let nDotD = dot(dir, N);
      if (nDotD < 0.0) { dir = -dir; }
      let cosT = abs(nDotD);

      // Ближе к точке — плотнее (корень из доли), дальний край — на радиусе.
      let len = U.uRadiusWorld * sqrt((fi + 0.5) / f32(maxSamples));
      let S = P + dir * len;

      // Косинусный вес — как в самом определении затенённости полусферы.
      // Вес начисляется ДО отбраковки по экрану: точка за кадром — это
      // «не заслонена», а не «не существует», иначе край кадра темнел бы.
      count += cosT;

      let clip = U.uViewProj * vec4f(S, 1.0);
      if (clip.w <= 0.0) { continue; }
      let ndcS = clip.xyz / clip.w;
      let sUV = vec2f(ndcS.x * 0.5 + 0.5, 1.0 - (ndcS.y * 0.5 + 0.5));
      if (sUV.x < 0.0 || sUV.x > 1.0 || sUV.y < 0.0 || sUV.y > 1.0) { continue; }

      let sampleD = ndcS.z * 0.5 + 0.5;
      let sceneD = getDepth(sUV);
      if (sceneD >= 0.999) { continue; }          // фон не заслоняет

      // Геометрия ПЕРЕД точкой выборки — заслон; слишком далеко перед ней —
      // чужая поверхность (силуэт переднего плана), окно её отсекает.
      let dz = sampleD - sceneD;
      if (dz > U.uBias && dz < U.uDepthWindow) {
        occlusion += cosT * (1.0 - smoothstep(U.uDepthWindow * 0.25, U.uDepthWindow, dz));
      }
    }
  } else {
    // ── Откат: экранный диск по разностям глубины — ровно то, что было до
    // 10.19.21. Работает без нормалей и без матриц камеры: на старте, пока
    // карта нормалей не доехала, и на данных, где градиент вырожден.
    for (var i = 0; i < 32; i++) {
      if (i >= maxSamples) { break; }
      let fi = f32(i);
      // Спираль Вогеля: равномерное покрытие диска без явного паттерна
      let angle = fi * GOLDEN_ANGLE + jitter * TAU;
      let radius = sqrt((fi + 0.5) / f32(maxSamples)) * U.uRadius;
      let offset = vec2f(cos(angle), sin(angle)) * radius * texel;

      let sampleDepth = getDepth(uv + offset);
      let delta = depth - sampleDepth;

      // Считаются ВСЕ выборки, а заслоняющие добавляют вес. До 10.19.18 в
      // знаменатель шло число ЗАСЛОНЯЮЩИХ выборок — то есть при единственном
      // заслоне отношение равнялось единице, и затенение выходило максимальным
      // везде, где вообще есть перепад глубины. Отсюда и ровное потемнение всего
      // кадра, которое я принимал за «эффект».
      count += 1.0;
      if (delta > U.uBias && delta < U.uDepthWindow) {
        occlusion += 1.0 - smoothstep(U.uDepthWindow * 0.25, U.uDepthWindow, delta);
      }
    }
  }

  var ao = 1.0;
  if (count > 0.0) { ao = 1.0 - (occlusion / count) * U.uIntensity; }
  return vec4f(vec3f(clamp(ao, 0.0, 1.0)), 1.0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 5. AO v2 — билатеральный денойз + темпоральная репроекция по velocity
// ═══════════════════════════════════════════════════════════════════

export const FRAG_SSAO_DENOISE = /* wgsl */`
struct Params {
  uResolution: vec2f,
  uTexelSize: vec2f,
  uTemporalWeight: f32,
  uDepthSigma: f32,
  uSpatialSigma: f32,
  uHasDepth: i32,
  uEnableTemporal: i32,
  uHasVelocity: i32,
  uRadius: i32,
  uFrame: f32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var sampLinear: sampler;
@group(0) @binding(2) var sampNearest: sampler;
@group(0) @binding(3) var texScene: texture_2d<f32>;
@group(0) @binding(4) var texDepth: texture_2d<f32>;
@group(0) @binding(5) var texAO: texture_2d<f32>;
@group(0) @binding(6) var texPrevAO: texture_2d<f32>;
@group(0) @binding(7) var texPrevDepth: texture_2d<f32>;
@group(0) @binding(8) var texVelocity: texture_2d<f32>;

${DEPTH_HELPER}

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let centerDepth = getDepth(uv);

  // ── Пространственный этап: билатеральный фильтр по глубине ──
  var sum = 0.0;
  var weightSum = 0.0;
  let r = clamp(U.uRadius, 1, 3);
  let spatialFalloff = 1.0 / max(2.0 * U.uSpatialSigma * U.uSpatialSigma, 1e-4);

  for (var x = -3; x <= 3; x++) {
    if (x < -r || x > r) { continue; }
    for (var y = -3; y <= 3; y++) {
      if (y < -r || y > r) { continue; }
      let off = vec2f(f32(x), f32(y));
      let sampleUV = uv + off * U.uTexelSize;
      let z = getDepth(sampleUV);

      let wDepth = exp(-abs(z - centerDepth) * U.uDepthSigma);
      let wSpatial = exp(-dot(off, off) * spatialFalloff);
      let w = wDepth * wSpatial;

      sum += textureSampleLevel(texAO, sampLinear, sampleUV, 0.0).r * w;
      weightSum += w;
    }
  }

  var current = vec3f(select(1.0, sum / weightSum, weightSum > 1e-5));

  // ── Темпоральный этап: репроекция по вектору движения ──
  if (U.uEnableTemporal != 0 && U.uTemporalWeight > 0.001) {
    var prevUV = uv;
    if (U.uHasVelocity != 0) {
      let velocity = textureSampleLevel(texVelocity, sampLinear, uv, 0.0).rg;
      prevUV = uv - velocity;
    }

    // За границами кадра истории нет — берём только текущий результат
    let inBounds = prevUV.x >= 0.0 && prevUV.x <= 1.0 && prevUV.y >= 0.0 && prevUV.y <= 1.0;
    if (inBounds) {
      let prevAO = textureSampleLevel(texPrevAO, sampLinear, prevUV, 0.0).rgb;
      // 10.19.20: sampNearest, как и у всей глубины. Прошлая глубина хранится в
      // r32float, а фильтровать r32float умеет не каждое устройство
      // (нужна возможность float32-filterable). Слои глубины объявлены
      // нефильтруемыми — фильтрующая выборка сделала бы конвейер невалидным.
      let prevDepth = textureSampleLevel(texPrevDepth, sampNearest, prevUV, 0.0).r;

      // Расхождение глубины = разрыв истории (disocclusion). Чем оно больше,
      // тем сильнее доверяем текущему кадру, иначе тянется «призрак».
      let depthDiff = abs(centerDepth - prevDepth);
      let rejection = smoothstep(0.0, 0.08, depthDiff);
      let blend = mix(U.uTemporalWeight, 1.0, rejection);
      current = mix(prevAO, current, blend);
    }
  }

  return vec4f(current, 1.0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 6. AO APPLY — умножение сцены на карту окклюзии (сцена → linear)
// ═══════════════════════════════════════════════════════════════════

export const FRAG_AO_APPLY = /* wgsl */`
struct Params {
  uResolution: vec2f,
  uInputIsLinear: i32,
  uAOStrength: f32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var sampLinear: sampler;
@group(0) @binding(2) var sampNearest: sampler;
@group(0) @binding(3) var texScene: texture_2d<f32>;
@group(0) @binding(4) var texAO: texture_2d<f32>;

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  var scene = textureSampleLevel(texScene, sampLinear, uv, 0.0).rgb;
  if (U.uInputIsLinear == 0) { scene = srgbToLinear(scene); }
  let ao = textureSampleLevel(texAO, sampLinear, uv, 0.0).rgb;
  let shaded = scene * mix(vec3f(1.0), ao, U.uAOStrength);
  return vec4f(shaded, 1.0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 7. VOLUMETRIC LIGHTING — настоящий ray marching с теневым тестом
// ═══════════════════════════════════════════════════════════════════

export const FRAG_VOLUMETRIC_LIGHT = /* wgsl */`
struct Params {
  uInvViewProj: mat4x4f,
  uViewProj: mat4x4f,
  uLight0: vec4f,      // xyz — позиция в мире, w — интенсивность
  uLight1: vec4f,
  uLight2: vec4f,
  uLight3: vec4f,
  uLightColor0: vec4f, // rgb — цвет, a — радиус влияния
  uLightColor1: vec4f,
  uLightColor2: vec4f,
  uLightColor3: vec4f,
  uCameraPos: vec4f,
  uResolution: vec2f,
  uIntensity: f32,
  uScattering: f32,     // коэффициент рассеяния среды
  uAbsorption: f32,     // коэффициент поглощения
  uAnisotropy: f32,     // g в фазовой функции Хеньи–Гринштейна
  uSteps: i32,
  uLightCount: i32,
  uHasDepth: i32,
  uShadowEnabled: i32,
  uMaxDistance: f32,
  uFrame: f32,
  // [10.19.21] Есть ли карта нормалей из объёма — для N·L по поверхности.
  uHasNormals: i32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var sampLinear: sampler;
@group(0) @binding(2) var sampNearest: sampler;
@group(0) @binding(3) var texScene: texture_2d<f32>;
@group(0) @binding(4) var texDepth: texture_2d<f32>;
@group(0) @binding(5) var texNormal: texture_2d<f32>;

${DEPTH_HELPER}
${NORMAL_HELPER}

// Восстановление мировой позиции из глубины и обратной матрицы вида-проекции
fn worldFromDepth(uv: vec2f, depth: f32) -> vec3f {
  // uv имеет перевёрнутый Y относительно NDC, возвращаем обратно
  let ndc = vec4f(uv.x * 2.0 - 1.0, (1.0 - uv.y) * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
  let world = U.uInvViewProj * ndc;
  return world.xyz / max(world.w, 1e-6);
}

fn lightAt(i: i32) -> vec4f {
  if (i == 0) { return U.uLight0; }
  if (i == 1) { return U.uLight1; }
  if (i == 2) { return U.uLight2; }
  return U.uLight3;
}

fn lightColorAt(i: i32) -> vec4f {
  if (i == 0) { return U.uLightColor0; }
  if (i == 1) { return U.uLightColor1; }
  if (i == 2) { return U.uLightColor2; }
  return U.uLightColor3;
}

// Теневой тест в screen space: проецируем точку луча обратно на экран и
// сравниваем её глубину с записанной в буфере. Точка «в тени», если перед
// ней есть геометрия. Не так точно, как shadow map, но не требует второго
// прохода рендера и работает с уже имеющимся depth-буфером.
fn shadowTest(worldPos: vec3f) -> f32 {
  if (U.uShadowEnabled == 0 || U.uHasDepth == 0) { return 1.0; }

  let clip = U.uViewProj * vec4f(worldPos, 1.0);
  if (clip.w <= 0.0) { return 1.0; }

  let ndc = clip.xyz / clip.w;
  let sUV = vec2f(ndc.x * 0.5 + 0.5, 1.0 - (ndc.y * 0.5 + 0.5));
  if (sUV.x < 0.0 || sUV.x > 1.0 || sUV.y < 0.0 || sUV.y > 1.0) { return 1.0; }

  let sceneDepth = getDepth(sUV);
  let pointDepth = ndc.z * 0.5 + 0.5;

  // Смещение против self-shadowing (acne)
  let bias = 0.003;
  return select(1.0, 0.0, pointDepth > sceneDepth + bias);
}

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let sceneColor = textureSampleLevel(texScene, sampLinear, uv, 0.0).rgb;

  if (U.uIntensity < 0.001 || U.uLightCount <= 0) {
    return vec4f(sceneColor, 1.0);
  }

  let depth = getDepth(uv);
  let camera = U.uCameraPos.xyz;

  // Конец луча — поверхность (или дальняя граница, если глубины нет)
  let surfaceWorld = worldFromDepth(uv, select(0.98, depth, U.uHasDepth != 0));
  let rayVec = surfaceWorld - camera;
  let rayLen = min(length(rayVec), U.uMaxDistance);

  if (rayLen < 1e-4) { return vec4f(sceneColor, 1.0); }

  let rayDir = rayVec / max(length(rayVec), 1e-6);
  let steps = clamp(U.uSteps, 4, 128);
  let stepLen = rayLen / f32(steps);

  // Дизеринг стартового смещения убирает кольцевые артефакты (banding),
  // превращая их в шум, который снимается денойзером
  let dither = ignNoise(uv * U.uResolution, U.uFrame);

  var scattered = vec3f(0.0);
  var transmittance = 1.0;
  let extinction = U.uScattering + U.uAbsorption;

  for (var s = 0; s < 128; s++) {
    if (s >= steps) { break; }
    if (transmittance < 0.01) { break; }

    let t = (f32(s) + dither) * stepLen;
    let samplePos = camera + rayDir * t;

    // Вклад каждого источника в точке луча
    var inScatter = vec3f(0.0);
    for (var li = 0; li < 4; li++) {
      if (li >= U.uLightCount) { break; }

      let lightData = lightAt(li);
      let colorData = lightColorAt(li);
      let toLight = lightData.xyz - samplePos;
      let distToLight = length(toLight);
      let lightDir = toLight / max(distToLight, 1e-6);

      // Квадратичное затухание с мягким обрезанием по радиусу
      let radius = max(colorData.a, 1e-3);
      let attenuation = 1.0 / (1.0 + distToLight * distToLight / (radius * radius));
      let rangeFade = 1.0 - smoothstep(radius * 0.7, radius, distToLight);

      let phase = henyeyGreenstein(dot(rayDir, lightDir), U.uAnisotropy);
      let shadow = shadowTest(samplePos);

      inScatter += colorData.rgb * lightData.w * attenuation * rangeFade * phase * shadow;
    }

    // Интегрирование по Бугеру–Ламберту–Беру на шаге
    let stepTransmittance = exp(-extinction * stepLen);
    scattered += inScatter * U.uScattering * transmittance * stepLen;
    transmittance *= stepTransmittance;
  }

  // ── [10.19.21] НАСТОЯЩИЙ N·L ПО ПОВЕРХНОСТИ. ──
  //
  // До сих пор источники объёмного света рождали ТОЛЬКО туман: поверхность на
  // них не отзывалась, потому что нормали не было (depthNormal — экранная
  // поделка с осью z, приколоченной к единице, для освещения она не годится).
  // Теперь нормаль настоящая — градиент непрозрачности самого объёма — и
  // поверхность освещается по Ламберту: цвет сцены выступает альбедо, добавка
  // равна альбедо · Σ (N·L) · цвет · затухание · тень.
  var surface = vec3f(0.0);
  if (U.uHasNormals != 0 && U.uHasDepth != 0 && depth < 0.999) {
    let nv = getNormal(uv);
    if (nv.w > 0.5) {
      let N = nv.xyz;
      for (var li = 0; li < 4; li++) {
        if (li >= U.uLightCount) { break; }
        let lightData = lightAt(li);
        let colorData = lightColorAt(li);
        let toLight = lightData.xyz - surfaceWorld;
        let distToLight = length(toLight);
        let L = toLight / max(distToLight, 1e-6);
        let lambert = max(dot(N, L), 0.0);
        if (lambert <= 0.0) { continue; }

        let radius = max(colorData.a, 1e-3);
        let attenuation = 1.0 / (1.0 + distToLight * distToLight / (radius * radius));
        let rangeFade = 1.0 - smoothstep(radius * 0.7, radius, distToLight);
        // Тень проверяется НЕ в самой точке поверхности: она сидит в буфере
        // глубины и заслонила бы сама себя. Сдвиг вдоль нормали убирает это.
        let sh = shadowTest(surfaceWorld + N * 1.5);
        surface += colorData.rgb * lightData.w * attenuation * rangeFade * lambert * sh;
      }
    }
  }

  let result = sceneColor * transmittance
             + sceneColor * surface * U.uIntensity
             + scattered * U.uIntensity;
  return vec4f(result, 1.0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 8. SSGI — вторичное освещение в screen space
// ═══════════════════════════════════════════════════════════════════

export const FRAG_SSGI = /* wgsl */`
struct Params {
  uResolution: vec2f,
  uTexelSize: vec2f,
  uIntensity: f32,
  uRadius: f32,
  uThickness: f32,
  uSamples: i32,
  uBounces: i32,
  uHasDepth: i32,
  uFrame: f32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var sampLinear: sampler;
@group(0) @binding(2) var sampNearest: sampler;
@group(0) @binding(3) var texScene: texture_2d<f32>;
@group(0) @binding(4) var texDepth: texture_2d<f32>;

${DEPTH_HELPER}

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let centerDepth = getDepth(uv);
  let centerColor = textureSampleLevel(texScene, sampLinear, uv, 0.0).rgb;

  if (U.uIntensity < 0.001 || centerDepth < 0.02) {
    return vec4f(vec3f(0.0), 1.0);
  }

  let normal = depthNormal(uv, U.uTexelSize);
  let jitter = ignNoise(uv * U.uResolution, U.uFrame);
  let samples = clamp(U.uSamples, 4, 32);

  var gathered = vec3f(0.0);
  var weightSum = 0.0;

  for (var i = 0; i < 32; i++) {
    if (i >= samples) { break; }
    let fi = f32(i);

    // Спираль Вогеля в экранной плоскости
    let angle = fi * GOLDEN_ANGLE + jitter * TAU;
    let dist = sqrt((fi + 0.5) / f32(samples)) * U.uRadius;
    let offset = vec2f(cos(angle), sin(angle)) * dist * U.uTexelSize;
    let sampleUV = uv + offset;

    if (sampleUV.x < 0.0 || sampleUV.x > 1.0 || sampleUV.y < 0.0 || sampleUV.y > 1.0) { continue; }

    let sampleDepth = getDepth(sampleUV);
    let sampleColor = textureSampleLevel(texScene, sampLinear, sampleUV, 0.0).rgb;

    // Отбрасываем сэмплы за «толщиной» — они принадлежат другой поверхности
    let depthDelta = centerDepth - sampleDepth;
    if (abs(depthDelta) > U.uThickness) { continue; }

    // Направление на сэмпл в экранном пространстве, поднятое в 3D по глубине
    let sampleDir = normalize(vec3f(offset * U.uResolution, depthDelta * U.uResolution.y));
    let cosTheta = max(dot(normal, sampleDir), 0.0);
    if (cosTheta <= 0.001) { continue; }

    // Яркие сэмплы отдают больше отражённого света
    let energy = luma(sampleColor);
    let falloff = 1.0 / (1.0 + dist * 0.15);
    let w = cosTheta * falloff;

    gathered += sampleColor * energy * w;
    weightSum += w;
  }

  var gi = vec3f(0.0);
  if (weightSum > 1e-5) {
    gi = gathered / weightSum;
    // Второй отскок аппроксимируется переизлучением уже собранного света
    if (U.uBounces > 1) {
      let secondBounce = gi * centerColor * 0.35;
      gi += secondBounce * f32(min(U.uBounces - 1, 3));
    }
  }

  return vec4f(gi * U.uIntensity, 1.0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 9. SSGI RESOLVE — темпоральная аккумуляция + применение к сцене
// ═══════════════════════════════════════════════════════════════════

export const FRAG_SSGI_RESOLVE = /* wgsl */`
struct Params {
  uResolution: vec2f,
  uTexelSize: vec2f,
  uTemporalWeight: f32,
  uIntensity: f32,
  uHasVelocity: i32,
  uHasDepth: i32,
  uEnableTemporal: i32,
  uFrame: f32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var sampLinear: sampler;
@group(0) @binding(2) var sampNearest: sampler;
@group(0) @binding(3) var texScene: texture_2d<f32>;
@group(0) @binding(4) var texDepth: texture_2d<f32>;
@group(0) @binding(5) var texGI: texture_2d<f32>;
@group(0) @binding(6) var texPrevGI: texture_2d<f32>;
@group(0) @binding(7) var texVelocity: texture_2d<f32>;

${DEPTH_HELPER}

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  var gi = textureSampleLevel(texGI, sampLinear, uv, 0.0).rgb;

  if (U.uEnableTemporal != 0 && U.uTemporalWeight > 0.001) {
    var prevUV = uv;
    if (U.uHasVelocity != 0) {
      prevUV = uv - textureSampleLevel(texVelocity, sampLinear, uv, 0.0).rg;
    }

    let inBounds = prevUV.x >= 0.0 && prevUV.x <= 1.0 && prevUV.y >= 0.0 && prevUV.y <= 1.0;
    if (inBounds) {
      let prevGI = textureSampleLevel(texPrevGI, sampLinear, prevUV, 0.0).rgb;

      // Ограничение по соседям (neighbourhood clamping) — история зажимается
      // в диапазон текущего кадра, иначе за движущимися объектами тянется шлейф
      var minC = gi;
      var maxC = gi;
      for (var x = -1; x <= 1; x++) {
        for (var y = -1; y <= 1; y++) {
          let n = textureSampleLevel(texGI, sampLinear,
            uv + vec2f(f32(x), f32(y)) * U.uTexelSize, 0.0).rgb;
          minC = min(minC, n);
          maxC = max(maxC, n);
        }
      }
      let clamped = clamp(prevGI, minC, maxC);
      gi = mix(clamped, gi, 1.0 - U.uTemporalWeight);
    }
  }

  let scene = textureSampleLevel(texScene, sampLinear, uv, 0.0).rgb;
  return vec4f(scene + gi * U.uIntensity, 1.0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 10. VELOCITY — буфер векторов движения из репроекции глубины
// ═══════════════════════════════════════════════════════════════════

export const FRAG_VELOCITY = /* wgsl */`
struct Params {
  uInvViewProj: mat4x4f,
  uPrevViewProj: mat4x4f,
  uResolution: vec2f,
  uScale: f32,
  uMaxVelocity: f32,
  uHasDepth: i32,
  uHasMatrices: i32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var sampLinear: sampler;
@group(0) @binding(2) var sampNearest: sampler;
@group(0) @binding(3) var texScene: texture_2d<f32>;
@group(0) @binding(4) var texDepth: texture_2d<f32>;

${DEPTH_HELPER}

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  // Без матриц камеры честный вектор движения не восстановить —
  // возвращаем ноль, потребители (motion blur, TAA) это корректно обрабатывают
  if (U.uHasMatrices == 0) { return vec4f(0.0, 0.0, 0.0, 1.0); }

  let depth = getDepth(uv);
  let ndc = vec4f(uv.x * 2.0 - 1.0, (1.0 - uv.y) * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);

  let world = U.uInvViewProj * ndc;
  let worldPos = world.xyz / max(world.w, 1e-6);

  let prevClip = U.uPrevViewProj * vec4f(worldPos, 1.0);
  if (abs(prevClip.w) < 1e-6) { return vec4f(0.0, 0.0, 0.0, 1.0); }

  let prevNDC = prevClip.xy / prevClip.w;
  let prevUV = vec2f(prevNDC.x * 0.5 + 0.5, 1.0 - (prevNDC.y * 0.5 + 0.5));

  var velocity = (uv - prevUV) * U.uScale;

  // Обрезание по длине: сверхдлинные векторы означают ошибку репроекции
  let len = length(velocity);
  if (len > U.uMaxVelocity) { velocity = velocity * (U.uMaxVelocity / len); }

  return vec4f(velocity, 0.0, 1.0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 11. MOTION BLUR — размытие вдоль вектора движения
// ═══════════════════════════════════════════════════════════════════

export const FRAG_MOTION_BLUR = /* wgsl */`
struct Params {
  uResolution: vec2f,
  uTexelSize: vec2f,
  uIntensity: f32,
  uSamples: i32,
  uHasVelocity: i32,
  uHasDepth: i32,
  uShutterAngle: f32,
  uFrame: f32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var sampLinear: sampler;
@group(0) @binding(2) var sampNearest: sampler;
@group(0) @binding(3) var texScene: texture_2d<f32>;
@group(0) @binding(4) var texDepth: texture_2d<f32>;
@group(0) @binding(5) var texVelocity: texture_2d<f32>;

${DEPTH_HELPER}

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let center = textureSampleLevel(texScene, sampLinear, uv, 0.0).rgb;

  if (U.uIntensity < 0.001 || U.uHasVelocity == 0) {
    return vec4f(center, 1.0);
  }

  // Угол затвора переводит вектор смещения за кадр в длину следа:
  // 180° (значение 0.5) — киношный стандарт
  let velocity = textureSampleLevel(texVelocity, sampLinear, uv, 0.0).rg
                 * U.uIntensity * U.uShutterAngle;

  // Смещение меньше пикселя — размывать нечего
  if (length(velocity * U.uResolution) < 1.0) {
    return vec4f(center, 1.0);
  }

  let samples = clamp(U.uSamples, 4, 32);
  let centerDepth = getDepth(uv);
  let jitter = ignNoise(uv * U.uResolution, U.uFrame) - 0.5;

  var accum = center;
  var weightSum = 1.0;

  for (var i = 1; i < 32; i++) {
    if (i >= samples) { break; }

    // Симметричная выборка по обе стороны от пикселя
    let t = (f32(i) / f32(samples - 1) - 0.5 + jitter / f32(samples));
    let sampleUV = uv + velocity * t;

    if (sampleUV.x < 0.0 || sampleUV.x > 1.0 || sampleUV.y < 0.0 || sampleUV.y > 1.0) { continue; }

    // Сэмплы с сильно другой глубиной принадлежат другому объекту —
    // без этой проверки фон «затекает» на передний план
    let sampleDepth = getDepth(sampleUV);
    let depthWeight = exp(-abs(sampleDepth - centerDepth) * 12.0);
    let w = depthWeight * (1.0 - abs(t) * 0.5);

    accum += textureSampleLevel(texScene, sampLinear, sampleUV, 0.0).rgb * w;
    weightSum += w;
  }

  return vec4f(accum / weightSum, 1.0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 12. SUBSURFACE SCATTERING — pre-integrated модель для тканей
// ═══════════════════════════════════════════════════════════════════

export const FRAG_SSS = /* wgsl */`
struct Params {
  uResolution: vec2f,
  uTexelSize: vec2f,
  uStrength: f32,
  uRadius: f32,
  uFalloffR: f32,
  uFalloffG: f32,
  uFalloffB: f32,
  uThicknessScale: f32,
  uSamples: i32,
  uHasDepth: i32,
  uFrame: f32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var sampLinear: sampler;
@group(0) @binding(2) var sampNearest: sampler;
@group(0) @binding(3) var texScene: texture_2d<f32>;
@group(0) @binding(4) var texDepth: texture_2d<f32>;

${DEPTH_HELPER}

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let center = textureSampleLevel(texScene, sampLinear, uv, 0.0).rgb;

  if (U.uStrength < 0.001) { return vec4f(center, 1.0); }

  let centerDepth = getDepth(uv);
  if (centerDepth < 0.02) { return vec4f(center, 1.0); }

  // Оценка толщины: пологая геометрия (малый градиент глубины) означает
  // толстый участок, где свет уходит вглубь; резкий край — тонкий
  let dx = getDepth(uv + vec2f(U.uTexelSize.x, 0.0)) - getDepth(uv - vec2f(U.uTexelSize.x, 0.0));
  let dy = getDepth(uv + vec2f(0.0, U.uTexelSize.y)) - getDepth(uv - vec2f(0.0, U.uTexelSize.y));
  let gradient = length(vec2f(dx, dy)) * U.uResolution.y;
  let thickness = clamp(1.0 - gradient * U.uThicknessScale, 0.0, 1.0);

  // Длина свободного пробега различается по каналам: красный уходит глубже
  // всего, поэтому подповерхностное свечение тканей всегда красноватое
  let falloff = vec3f(U.uFalloffR, U.uFalloffG, U.uFalloffB);
  let samples = clamp(U.uSamples, 4, 24);
  let jitter = ignNoise(uv * U.uResolution, U.uFrame);

  var accum = vec3f(0.0);
  var weightSum = vec3f(0.0);

  for (var i = 0; i < 24; i++) {
    if (i >= samples) { break; }
    let fi = f32(i);

    let angle = fi * GOLDEN_ANGLE + jitter * TAU;
    let dist = sqrt((fi + 0.5) / f32(samples));
    let offset = vec2f(cos(angle), sin(angle)) * dist * U.uRadius * U.uTexelSize * 8.0;
    let sampleUV = uv + offset;

    let sampleDepth = getDepth(sampleUV);
    // Разрыв глубины — другая поверхность, свет через неё не проходит
    let depthGate = exp(-abs(sampleDepth - centerDepth) * 30.0);

    // Гауссово ядро своей ширины на канал
    let r2 = dist * dist;
    let w = exp(-r2 * falloff) * depthGate;

    accum += textureSampleLevel(texScene, sampLinear, sampleUV, 0.0).rgb * w;
    weightSum += w;
  }

  let scattered = accum / max(weightSum, vec3f(1e-4));
  let blended = mix(center, scattered, U.uStrength * thickness);
  return vec4f(blended, 1.0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 13. BRIGHT PASS
// ═══════════════════════════════════════════════════════════════════

export const FRAG_BRIGHT = /* wgsl */`
struct Params {
  uThreshold: f32,
  uSoftKnee: f32,
  uInputIsLinear: i32,
  uClampMax: f32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var sampLinear: sampler;
@group(0) @binding(2) var sampNearest: sampler;
@group(0) @binding(3) var texScene: texture_2d<f32>;

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  var c = textureSampleLevel(texScene, sampLinear, uv, 0.0).rgb;
  if (U.uInputIsLinear == 0) { c = srgbToLinear(c); }

  let br = luma(c);
  let knee = 2.0 * U.uSoftKnee + 0.0001;
  let s = clamp((br - U.uThreshold + U.uSoftKnee) / knee, 0.0, 1.0);
  let contribution = max(s * s, select(0.0, 1.0, br >= U.uThreshold));

  // Ограничение сверху гасит «светлячков» — одиночные сверхъяркие пиксели,
  // которые после блюра превращаются в мерцающие пятна
  let limited = min(c, vec3f(U.uClampMax));
  return vec4f(limited * contribution, 1.0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 14. GAUSSIAN BLUR (раздельный)
// ═══════════════════════════════════════════════════════════════════

export const FRAG_BLUR = /* wgsl */`
struct Params {
  uTexelSize: vec2f,
  uDirection: vec2f,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var sampLinear: sampler;
@group(0) @binding(2) var sampNearest: sampler;
@group(0) @binding(3) var texSrc: texture_2d<f32>;

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let off = U.uDirection * U.uTexelSize;
  var c = textureSampleLevel(texSrc, sampLinear, uv, 0.0).rgb * 0.227027;

  c += textureSampleLevel(texSrc, sampLinear, uv + off, 0.0).rgb * 0.1945946;
  c += textureSampleLevel(texSrc, sampLinear, uv - off, 0.0).rgb * 0.1945946;
  c += textureSampleLevel(texSrc, sampLinear, uv + off * 2.0, 0.0).rgb * 0.1216216;
  c += textureSampleLevel(texSrc, sampLinear, uv - off * 2.0, 0.0).rgb * 0.1216216;
  c += textureSampleLevel(texSrc, sampLinear, uv + off * 3.0, 0.0).rgb * 0.054054;
  c += textureSampleLevel(texSrc, sampLinear, uv - off * 3.0, 0.0).rgb * 0.054054;
  c += textureSampleLevel(texSrc, sampLinear, uv + off * 4.0, 0.0).rgb * 0.016216;
  c += textureSampleLevel(texSrc, sampLinear, uv - off * 4.0, 0.0).rgb * 0.016216;

  return vec4f(c, 1.0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 15. GOD RAYS
// ═══════════════════════════════════════════════════════════════════

export const FRAG_GODRAYS = /* wgsl */`
struct Params {
  uLightPos: vec2f,
  uDensity: f32,
  uDecay: f32,
  uWeight: f32,
  uExposure: f32,
  uSamples: i32,
  uFrame: f32,
  uResolution: vec2f,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var sampLinear: sampler;
@group(0) @binding(2) var sampNearest: sampler;
@group(0) @binding(3) var texBright: texture_2d<f32>;

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let samples = clamp(U.uSamples, 8, 64);
  let delta = (uv - U.uLightPos) * (1.0 / f32(samples)) * U.uDensity;

  // Дизеринг старта убирает ступенчатые «кольца» вокруг источника
  let jitter = ignNoise(uv * U.uResolution, U.uFrame);
  var tc = uv - delta * jitter;

  var illumination = 1.0;
  var color = vec3f(0.0);

  for (var i = 0; i < 64; i++) {
    if (i >= samples) { break; }
    color += textureSampleLevel(texBright, sampLinear, tc, 0.0).rgb * illumination * U.uWeight;
    tc -= delta;
    illumination *= U.uDecay;
  }

  return vec4f(color * U.uExposure, 1.0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 16. SSR — отражения в screen space
// ═══════════════════════════════════════════════════════════════════

export const FRAG_SSR = /* wgsl */`
struct Params {
  uResolution: vec2f,
  uIntensity: f32,
  uRoughness: f32,
  uMaxDist: f32,
  uSteps: i32,
  uHasDepth: i32,
  uFrame: f32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var sampLinear: sampler;
@group(0) @binding(2) var sampNearest: sampler;
@group(0) @binding(3) var texScene: texture_2d<f32>;
@group(0) @binding(4) var texDepth: texture_2d<f32>;

${DEPTH_HELPER}

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  var col = textureSampleLevel(texScene, sampLinear, uv, 0.0).rgb;
  let depth = getDepth(uv);

  if (depth < 0.05 || U.uIntensity < 0.001) { return vec4f(col, 1.0); }

  let texel = vec2f(1.0) / U.uResolution;
  let ddy = getDepth(uv + vec2f(0.0, texel.y)) - getDepth(uv - vec2f(0.0, texel.y));
  let reflStrength = smoothstep(0.0, 0.05, abs(ddy)) * depth;

  if (reflStrength > 0.01) {
    var refl = vec3f(0.0);
    var totalWeight = 0.0;
    let steps = clamp(U.uSteps, 4, 32);
    let jitter = ignNoise(uv * U.uResolution, U.uFrame);

    for (var i = 1; i <= 32; i++) {
      if (i > steps) { break; }
      let fi = f32(i);

      let noise = (hash21(uv * U.uResolution + vec2f(fi, jitter)) - 0.5) * U.uRoughness * 0.02;
      let sampleUV = uv - vec2f(noise, fi * texel.y * U.uMaxDist / f32(steps));

      if (sampleUV.y < 0.0 || sampleUV.y > 1.0) { break; }

      let w = 1.0 / (1.0 + fi * 0.3);
      refl += textureSampleLevel(texScene, sampLinear, sampleUV, 0.0).rgb * w;
      totalWeight += w;
    }

    if (totalWeight > 0.0) {
      refl /= totalWeight;
      col = mix(col, refl, reflStrength * U.uIntensity * 0.5);
    }
  }

  return vec4f(col, 1.0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 17. VOLUMETRIC FOG — высотный туман (лёгкий, из v9.1)
// ═══════════════════════════════════════════════════════════════════

export const FRAG_VOL_FOG = /* wgsl */`
struct Params {
  uResolution: vec2f,
  uFogColor: vec3f,
  uDensity: f32,
  uFalloff: f32,
  uHeight: f32,
  uTime: f32,
  uHasDepth: i32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var sampLinear: sampler;
@group(0) @binding(2) var sampNearest: sampler;
@group(0) @binding(3) var texScene: texture_2d<f32>;
@group(0) @binding(4) var texDepth: texture_2d<f32>;

${DEPTH_HELPER}

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  var col = textureSampleLevel(texScene, sampLinear, uv, 0.0).rgb;
  let depth = getDepth(uv);

  let heightFactor = smoothstep(U.uHeight, 1.0, uv.y);
  let depthFactor = smoothstep(0.1, 0.8, depth);
  var fog = heightFactor * depthFactor * U.uDensity;

  let noise = hash21(uv * U.uResolution * 0.1 + vec2f(U.uTime * 0.5)) * 0.05;
  fog = pow(max(fog + noise, 0.0), U.uFalloff);
  fog = clamp(fog, 0.0, 0.7);

  col = mix(col, U.uFogColor, fog);
  return vec4f(col, 1.0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 18. COLOR LUT — плёночные профили цветокоррекции
// ═══════════════════════════════════════════════════════════════════

export const FRAG_COLOR_LUT = /* wgsl */`
struct Params {
  uLutMode: i32,
  uIntensity: f32,
  uMidGray: f32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var sampLinear: sampler;
@group(0) @binding(2) var sampNearest: sampler;
@group(0) @binding(3) var texScene: texture_2d<f32>;

fn lutKodak(cIn: vec3f) -> vec3f {
  var c = cIn;
  c.r = smoothstep(-0.05, 1.1, c.r) * 1.05;
  c.g = smoothstep(0.0, 1.05, c.g);
  c.b = smoothstep(0.05, 0.95, c.b) * 0.95;
  let shadows = vec3f(0.06, 0.04, 0.08);
  let l = luma(c);
  c = mix(c + shadows * (1.0 - l), c, smoothstep(0.0, 0.3, l));
  return c;
}

fn lutMedCool(cIn: vec3f) -> vec3f {
  var c = cIn;
  let l = luma(c);
  c.r *= mix(0.85, 1.0, l);
  c.b *= mix(1.15, 1.0, l);
  c = mix(vec3f(l), c, 0.85);
  return c;
}

fn lutWarmAmber(cIn: vec3f) -> vec3f {
  var c = cIn;
  let l = luma(c);
  c.r *= mix(1.1, 1.02, l);
  c.g *= mix(0.98, 1.0, l);
  c.b *= mix(0.75, 0.92, l);
  return c;
}

fn lutTeal(cIn: vec3f) -> vec3f {
  var c = cIn;
  let l = luma(c);
  c.r *= mix(1.2, 0.95, smoothstep(0.3, 0.7, l));
  c.g *= mix(0.9, 1.05, smoothstep(0.2, 0.6, l));
  c.b *= mix(1.15, 0.9, smoothstep(0.4, 0.8, l));
  return c;
}

fn lutBleach(c: vec3f) -> vec3f {
  let l = luma(c);
  let desat = mix(vec3f(l), c, 0.4);
  // 10.19.17: было mix(vec3f(0.5), desat, 1.4) — та же ось 0,5 в линейном
  // пространстве, что и в composite, и та же чёрная дыра для тёмных пикселей.
  return contrastAboutPivot(desat, 1.4, U.uMidGray);
}

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let col = textureSampleLevel(texScene, sampLinear, uv, 0.0).rgb;
  var graded = col;

  if (U.uLutMode == 1) { graded = lutKodak(col); }
  else if (U.uLutMode == 2) { graded = lutMedCool(col); }
  else if (U.uLutMode == 3) { graded = lutWarmAmber(col); }
  else if (U.uLutMode == 4) { graded = lutTeal(col); }
  else if (U.uLutMode == 5) { graded = lutBleach(col); }

  return vec4f(mix(col, graded, U.uIntensity), 1.0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 19. BOKEH DOF — кольцевая выборка с подъёмом ярких точек
// ═══════════════════════════════════════════════════════════════════

export const FRAG_BOKEH_DOF = /* wgsl */`
struct Params {
  uResolution: vec2f,
  uIntensity: f32,
  uFocus: f32,
  uRange: f32,
  uBokehSize: f32,
  uBokehBright: f32,
  uRings: i32,
  uHasDepth: i32,
  uFrame: f32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var sampLinear: sampler;
@group(0) @binding(2) var sampNearest: sampler;
@group(0) @binding(3) var texScene: texture_2d<f32>;
@group(0) @binding(4) var texDepth: texture_2d<f32>;

${DEPTH_HELPER}

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let col = textureSampleLevel(texScene, sampLinear, uv, 0.0).rgb;
  let depth = getDepth(uv);

  // Circle of confusion — насколько точка «расплывается» вне фокуса
  let coc = smoothstep(0.0, U.uRange, abs(depth - U.uFocus)) * U.uIntensity;
  if (coc < 0.01) { return vec4f(col, 1.0); }

  let texel = vec2f(1.0) / U.uResolution;
  let radius = coc * U.uBokehSize * 8.0;
  let rings = clamp(U.uRings, 2, 5);
  let jitter = ignNoise(uv * U.uResolution, U.uFrame);

  var sum = vec3f(0.0);
  var totalWeight = 0.0;

  for (var ring = 1; ring <= 5; ring++) {
    if (ring > rings) { break; }
    let r = f32(ring) * radius / f32(rings);
    let samplesInRing = ring * 6;

    for (var s = 0; s < 30; s++) {
      if (s >= samplesInRing) { break; }
      let angle = (f32(s) / f32(samplesInRing) + jitter) * TAU;
      let offset = vec2f(cos(angle), sin(angle)) * r * texel;

      let sc = textureSampleLevel(texScene, sampLinear, uv + offset, 0.0).rgb;
      // Яркие сэмплы весят больше — так получаются характерные боке-кружки
      let sLum = luma(sc);
      let w = 1.0 + max(0.0, sLum - 0.5) * U.uBokehBright;

      sum += sc * w;
      totalWeight += w;
    }
  }

  let blurred = select(col, sum / totalWeight, totalWeight > 0.0);
  return vec4f(mix(col, blurred, coc), 1.0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 20. COMPOSITE — сведение, тонмаппинг, грейдинг, оверлеи
// ═══════════════════════════════════════════════════════════════════

export const FRAG_COMPOSITE = /* wgsl */`
struct Params {
  uResolution: vec2f,
  uColorTint: vec3f,
  uEdgeColor: vec3f,
  uAIMaskColor: vec3f,
  uTime: f32,
  uExposure: f32,
  uGamma: f32,
  uBloomIntensity: f32,
  uGodRaysIntensity: f32,
  uVolumetricIntensity: f32,
  uSharpen: f32,
  uVignetteIntensity: f32,
  uVignetteRadius: f32,
  uSaturation: f32,
  uContrast: f32,
  uColorTemp: f32,
  uDofIntensity: f32,
  uDofFocus: f32,
  uDofRange: f32,
  uEdgeIntensity: f32,
  uChromAbIntensity: f32,
  uGrainIntensity: f32,
  uSplitPos: f32,
  uAIMaskOpacity: f32,
  uToneMapMode: i32,
  uInputIsLinear: i32,
  uHasVolumetric: i32,
  uFrame: f32,
  uMidGray: f32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var sampLinear: sampler;
@group(0) @binding(2) var sampNearest: sampler;
@group(0) @binding(3) var texScene: texture_2d<f32>;
@group(0) @binding(4) var texBloom: texture_2d<f32>;
@group(0) @binding(5) var texOriginal: texture_2d<f32>;
@group(0) @binding(6) var texGodRays: texture_2d<f32>;
@group(0) @binding(7) var texAIMask: texture_2d<f32>;
@group(0) @binding(8) var texVolumetric: texture_2d<f32>;

fn sceneLinear(uv: vec2f) -> vec3f {
  let c = textureSampleLevel(texScene, sampLinear, uv, 0.0).rgb;
  return select(srgbToLinear(c), c, U.uInputIsLinear != 0);
}

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let texel = vec2f(1.0) / U.uResolution;

  // ── Режим сравнения «до/после» с перетаскиваемым разделителем ──
  if (U.uSplitPos > 0.005 && U.uSplitPos < 0.995) {
    if (uv.x > U.uSplitPos) {
      if (abs(uv.x - U.uSplitPos) < 1.5 * texel.x) {
        return vec4f(1.0, 0.8, 0.0, 1.0);
      }
      return textureSampleLevel(texOriginal, sampLinear, uv, 0.0);
    }
    if (abs(uv.x - U.uSplitPos) < 1.5 * texel.x) {
      return vec4f(1.0, 0.8, 0.0, 1.0);
    }
  }

  var col = sceneLinear(uv);

  // ── Хроматическая аберрация ──
  if (U.uChromAbIntensity > 0.001) {
    let dir = (uv - vec2f(0.5)) * U.uChromAbIntensity * 0.02;
    col.r = sceneLinear(uv + dir).r;
    col.b = sceneLinear(uv - dir).b;
  }

  // ── Дешёвый DOF (полноценный — в отдельном bokeh-проходе) ──
  if (U.uDofIntensity > 0.001) {
    let d = luma(col);
    let fd = abs(d - U.uDofFocus);
    let blurAmount = smoothstep(0.0, U.uDofRange, fd) * U.uDofIntensity;

    if (blurAmount > 0.01) {
      var bl = vec3f(0.0);
      var t = 0.0;
      for (var x = -2; x <= 2; x++) {
        for (var y = -2; y <= 2; y++) {
          let w = 1.0 / (1.0 + f32(x * x + y * y));
          bl += sceneLinear(uv + vec2f(f32(x), f32(y)) * texel * blurAmount * 3.0) * w;
          t += w;
        }
      }
      col = mix(col, bl / t, blurAmount);
    }
  }

  // ── Резкость (unsharp mask) ──
  if (U.uSharpen > 0.001) {
    let b4 = (sceneLinear(uv + vec2f(-texel.x, 0.0)) + sceneLinear(uv + vec2f(texel.x, 0.0))
            + sceneLinear(uv + vec2f(0.0, -texel.y)) + sceneLinear(uv + vec2f(0.0, texel.y))) * 0.25;
    col += (col - b4) * U.uSharpen;
  }

  // ── Подсветка контуров ──
  if (U.uEdgeIntensity > 0.001) {
    let lL = luma(sceneLinear(uv - vec2f(texel.x, 0.0)));
    let lR = luma(sceneLinear(uv + vec2f(texel.x, 0.0)));
    let lU = luma(sceneLinear(uv - vec2f(0.0, texel.y)));
    let lD = luma(sceneLinear(uv + vec2f(0.0, texel.y)));
    let edge = smoothstep(0.02, 0.15, abs(lL - lR) + abs(lU - lD));
    col += U.uEdgeColor * edge * U.uEdgeIntensity;
  }

  // ── Аддитивные слои ──
  col += textureSampleLevel(texBloom, sampLinear, uv, 0.0).rgb * U.uBloomIntensity;

  if (U.uGodRaysIntensity > 0.001) {
    col += textureSampleLevel(texGodRays, sampLinear, uv, 0.0).rgb * U.uGodRaysIntensity;
  }

  if (U.uHasVolumetric != 0 && U.uVolumetricIntensity > 0.001) {
    col += textureSampleLevel(texVolumetric, sampLinear, uv, 0.0).rgb * U.uVolumetricIntensity;
  }

  // ── Экспозиция и тонмаппинг ──
  col *= U.uExposure;
  if (U.uToneMapMode == 1) { col = acesToneMap(col); }
  else if (U.uToneMapMode == 2) { col = reinhardToneMap(col); }
  else if (U.uToneMapMode == 3) { col = filmicToneMap(col); }

  // ── Цветокоррекция ──
  let lu = luma(col);
  col = mix(vec3f(lu), col, U.uSaturation);
  col.r *= 1.0 + U.uColorTemp * 0.1;
  col.b *= 1.0 - U.uColorTemp * 0.1;
  col *= U.uColorTint;
  col = contrastAboutPivot(col, U.uContrast, U.uMidGray);

  // ── Зерно ──
  if (U.uGrainIntensity > 0.001) {
    let g = ignNoise(uv * U.uResolution, U.uFrame) - 0.5;
    col += vec3f(g * U.uGrainIntensity * 0.15);
  }

  // ── Виньетка ──
  if (U.uVignetteIntensity > 0.001) {
    let d = length(uv - vec2f(0.5)) / U.uVignetteRadius;
    col *= 1.0 - smoothstep(0.5, 1.0, d) * U.uVignetteIntensity;
  }

  // ── Оверлей AI-маски (сегментация из gpuCompute.js) ──
  if (U.uAIMaskOpacity > 0.001) {
    let mask = textureSampleLevel(texAIMask, sampLinear, uv, 0.0).r;
    col = mix(col, U.uAIMaskColor, mask * U.uAIMaskOpacity);
  }

  col = max(col, vec3f(0.0));
  if (abs(U.uGamma - 2.2) > 0.01) {
    col = pow(col, vec3f(2.2 / U.uGamma));
  }

  return vec4f(linearToSrgb(col), 1.0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 21. FXAA
// ═══════════════════════════════════════════════════════════════════

export const FRAG_FXAA = /* wgsl */`
struct Params {
  uResolution: vec2f,
  uSpanMax: f32,
  uReduceMul: f32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var sampLinear: sampler;
@group(0) @binding(2) var sampNearest: sampler;
@group(0) @binding(3) var texSrc: texture_2d<f32>;

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let texel = vec2f(1.0) / U.uResolution;

  let rgbNW = textureSampleLevel(texSrc, sampLinear, uv + vec2f(-1.0, -1.0) * texel, 0.0).rgb;
  let rgbNE = textureSampleLevel(texSrc, sampLinear, uv + vec2f( 1.0, -1.0) * texel, 0.0).rgb;
  let rgbSW = textureSampleLevel(texSrc, sampLinear, uv + vec2f(-1.0,  1.0) * texel, 0.0).rgb;
  let rgbSE = textureSampleLevel(texSrc, sampLinear, uv + vec2f( 1.0,  1.0) * texel, 0.0).rgb;
  let rgbM  = textureSampleLevel(texSrc, sampLinear, uv, 0.0).rgb;

  let lumaNW = luma601(rgbNW);
  let lumaNE = luma601(rgbNE);
  let lumaSW = luma601(rgbSW);
  let lumaSE = luma601(rgbSE);
  let lumaM  = luma601(rgbM);

  let lumaMin = min(lumaM, min(min(lumaNW, lumaNE), min(lumaSW, lumaSE)));
  let lumaMax = max(lumaM, max(max(lumaNW, lumaNE), max(lumaSW, lumaSE)));

  var dir = vec2f(
    -((lumaNW + lumaNE) - (lumaSW + lumaSE)),
     ((lumaNW + lumaSW) - (lumaNE + lumaSE))
  );

  let dirReduce = max((lumaNW + lumaNE + lumaSW + lumaSE) * (0.25 * U.uReduceMul), 1.0 / 128.0);
  let rcpDirMin = 1.0 / (min(abs(dir.x), abs(dir.y)) + dirReduce);
  dir = clamp(dir * rcpDirMin, vec2f(-U.uSpanMax), vec2f(U.uSpanMax)) * texel;

  let rgbA = 0.5 * (
    textureSampleLevel(texSrc, sampLinear, uv + dir * (1.0 / 3.0 - 0.5), 0.0).rgb +
    textureSampleLevel(texSrc, sampLinear, uv + dir * (2.0 / 3.0 - 0.5), 0.0).rgb
  );
  let rgbB = rgbA * 0.5 + 0.25 * (
    textureSampleLevel(texSrc, sampLinear, uv - dir * 0.5, 0.0).rgb +
    textureSampleLevel(texSrc, sampLinear, uv + dir * 0.5, 0.0).rgb
  );

  let lumaB = luma601(rgbB);
  let result = select(rgbB, rgbA, lumaB < lumaMin || lumaB > lumaMax);
  return vec4f(result, 1.0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 22. BLIT — копирование / масштабирование
// ═══════════════════════════════════════════════════════════════════

export const FRAG_BLIT = /* wgsl */`
struct Params {
  uResolution: vec2f,
  uOpacity: f32,
  uUseNearest: i32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var sampLinear: sampler;
@group(0) @binding(2) var sampNearest: sampler;
@group(0) @binding(3) var texSrc: texture_2d<f32>;

@fragment
fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  var c: vec4f;
  if (U.uUseNearest != 0) {
    c = textureSampleLevel(texSrc, sampNearest, uv, 0.0);
  } else {
    c = textureSampleLevel(texSrc, sampLinear, uv, 0.0);
  }
  return vec4f(c.rgb, c.a * U.uOpacity);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 23. ДЕКЛАРАТИВНОЕ ОПИСАНИЕ ПРОХОДОВ
// ═══════════════════════════════════════════════════════════════════

/**
 * Каждый проход описывает: исходник фрагментного шейдера, порядок текстурных
 * биндингов (начиная с binding 3) и layout uniform-структуры.
 *
 * Порядок полей в `uniforms` ОБЯЗАН совпадать с порядком в WGSL-структуре —
 * иначе смещения разъедутся. Тест wgslLayoutMatchesJS() в проверочном наборе
 * сверяет оба описания автоматически.
 */
export const RENDER_PASSES = Object.freeze({
  ssao: {
    label: 'ssao',
    fragment: FRAG_SSAO,
    textures: ['texScene', 'texDepth', 'texNormal'],
    uniforms: [
      ['uViewProj', 'mat4x4f'],
      ['uInvViewProj', 'mat4x4f'],
      ['uResolution', 'vec2f'],
      ['uRadius', 'f32'],
      ['uIntensity', 'f32'],
      ['uBias', 'f32'],
      ['uSamples', 'i32'],
      ['uHasDepth', 'i32'],
      ['uFrame', 'f32'],
      ['uDepthWindow', 'f32'],
      ['uHasNormals', 'i32'],
      ['uRadiusWorld', 'f32'],
    ],
  },

  ssaoDenoise: {
    label: 'ssaoDenoise',
    fragment: FRAG_SSAO_DENOISE,
    textures: ['texScene', 'texDepth', 'texAO', 'texPrevAO', 'texPrevDepth', 'texVelocity'],
    uniforms: [
      ['uResolution', 'vec2f'],
      ['uTexelSize', 'vec2f'],
      ['uTemporalWeight', 'f32'],
      ['uDepthSigma', 'f32'],
      ['uSpatialSigma', 'f32'],
      ['uHasDepth', 'i32'],
      ['uEnableTemporal', 'i32'],
      ['uHasVelocity', 'i32'],
      ['uRadius', 'i32'],
      ['uFrame', 'f32'],
    ],
  },

  aoApply: {
    label: 'aoApply',
    fragment: FRAG_AO_APPLY,
    textures: ['texScene', 'texAO'],
    uniforms: [
      ['uResolution', 'vec2f'],
      ['uInputIsLinear', 'i32'],
      ['uAOStrength', 'f32'],
    ],
  },

  volumetricLight: {
    label: 'volumetricLight',
    fragment: FRAG_VOLUMETRIC_LIGHT,
    textures: ['texScene', 'texDepth', 'texNormal'],
    uniforms: [
      ['uInvViewProj', 'mat4x4f'],
      ['uViewProj', 'mat4x4f'],
      ['uLight0', 'vec4f'],
      ['uLight1', 'vec4f'],
      ['uLight2', 'vec4f'],
      ['uLight3', 'vec4f'],
      ['uLightColor0', 'vec4f'],
      ['uLightColor1', 'vec4f'],
      ['uLightColor2', 'vec4f'],
      ['uLightColor3', 'vec4f'],
      ['uCameraPos', 'vec4f'],
      ['uResolution', 'vec2f'],
      ['uIntensity', 'f32'],
      ['uScattering', 'f32'],
      ['uAbsorption', 'f32'],
      ['uAnisotropy', 'f32'],
      ['uSteps', 'i32'],
      ['uLightCount', 'i32'],
      ['uHasDepth', 'i32'],
      ['uShadowEnabled', 'i32'],
      ['uMaxDistance', 'f32'],
      ['uFrame', 'f32'],
      ['uHasNormals', 'i32'],
    ],
  },

  ssgi: {
    label: 'ssgi',
    fragment: FRAG_SSGI,
    textures: ['texScene', 'texDepth'],
    uniforms: [
      ['uResolution', 'vec2f'],
      ['uTexelSize', 'vec2f'],
      ['uIntensity', 'f32'],
      ['uRadius', 'f32'],
      ['uThickness', 'f32'],
      ['uSamples', 'i32'],
      ['uBounces', 'i32'],
      ['uHasDepth', 'i32'],
      ['uFrame', 'f32'],
    ],
  },

  ssgiResolve: {
    label: 'ssgiResolve',
    fragment: FRAG_SSGI_RESOLVE,
    textures: ['texScene', 'texDepth', 'texGI', 'texPrevGI', 'texVelocity'],
    uniforms: [
      ['uResolution', 'vec2f'],
      ['uTexelSize', 'vec2f'],
      ['uTemporalWeight', 'f32'],
      ['uIntensity', 'f32'],
      ['uHasVelocity', 'i32'],
      ['uHasDepth', 'i32'],
      ['uEnableTemporal', 'i32'],
      ['uFrame', 'f32'],
    ],
  },

  velocity: {
    label: 'velocity',
    fragment: FRAG_VELOCITY,
    textures: ['texScene', 'texDepth'],
    uniforms: [
      ['uInvViewProj', 'mat4x4f'],
      ['uPrevViewProj', 'mat4x4f'],
      ['uResolution', 'vec2f'],
      ['uScale', 'f32'],
      ['uMaxVelocity', 'f32'],
      ['uHasDepth', 'i32'],
      ['uHasMatrices', 'i32'],
    ],
  },

  motionBlur: {
    label: 'motionBlur',
    fragment: FRAG_MOTION_BLUR,
    textures: ['texScene', 'texDepth', 'texVelocity'],
    uniforms: [
      ['uResolution', 'vec2f'],
      ['uTexelSize', 'vec2f'],
      ['uIntensity', 'f32'],
      ['uSamples', 'i32'],
      ['uHasVelocity', 'i32'],
      ['uHasDepth', 'i32'],
      ['uShutterAngle', 'f32'],
      ['uFrame', 'f32'],
    ],
  },

  sss: {
    label: 'sss',
    fragment: FRAG_SSS,
    textures: ['texScene', 'texDepth'],
    uniforms: [
      ['uResolution', 'vec2f'],
      ['uTexelSize', 'vec2f'],
      ['uStrength', 'f32'],
      ['uRadius', 'f32'],
      ['uFalloffR', 'f32'],
      ['uFalloffG', 'f32'],
      ['uFalloffB', 'f32'],
      ['uThicknessScale', 'f32'],
      ['uSamples', 'i32'],
      ['uHasDepth', 'i32'],
      ['uFrame', 'f32'],
    ],
  },

  bright: {
    label: 'bright',
    fragment: FRAG_BRIGHT,
    textures: ['texScene'],
    uniforms: [
      ['uThreshold', 'f32'],
      ['uSoftKnee', 'f32'],
      ['uInputIsLinear', 'i32'],
      ['uClampMax', 'f32'],
    ],
  },

  blur: {
    label: 'blur',
    fragment: FRAG_BLUR,
    textures: ['texSrc'],
    uniforms: [
      ['uTexelSize', 'vec2f'],
      ['uDirection', 'vec2f'],
    ],
  },

  godRays: {
    label: 'godRays',
    fragment: FRAG_GODRAYS,
    textures: ['texBright'],
    uniforms: [
      ['uLightPos', 'vec2f'],
      ['uDensity', 'f32'],
      ['uDecay', 'f32'],
      ['uWeight', 'f32'],
      ['uExposure', 'f32'],
      ['uSamples', 'i32'],
      ['uFrame', 'f32'],
      ['uResolution', 'vec2f'],
    ],
  },

  ssr: {
    label: 'ssr',
    fragment: FRAG_SSR,
    textures: ['texScene', 'texDepth'],
    uniforms: [
      ['uResolution', 'vec2f'],
      ['uIntensity', 'f32'],
      ['uRoughness', 'f32'],
      ['uMaxDist', 'f32'],
      ['uSteps', 'i32'],
      ['uHasDepth', 'i32'],
      ['uFrame', 'f32'],
    ],
  },

  volFog: {
    label: 'volFog',
    fragment: FRAG_VOL_FOG,
    textures: ['texScene', 'texDepth'],
    uniforms: [
      ['uResolution', 'vec2f'],
      ['uFogColor', 'vec3f'],
      ['uDensity', 'f32'],
      ['uFalloff', 'f32'],
      ['uHeight', 'f32'],
      ['uTime', 'f32'],
      ['uHasDepth', 'i32'],
    ],
  },

  colorLut: {
    label: 'colorLut',
    fragment: FRAG_COLOR_LUT,
    textures: ['texScene'],
    uniforms: [
      ['uLutMode', 'i32'],
      ['uIntensity', 'f32'],
      ['uMidGray', 'f32'],
    ],
  },

  bokehDof: {
    label: 'bokehDof',
    fragment: FRAG_BOKEH_DOF,
    textures: ['texScene', 'texDepth'],
    uniforms: [
      ['uResolution', 'vec2f'],
      ['uIntensity', 'f32'],
      ['uFocus', 'f32'],
      ['uRange', 'f32'],
      ['uBokehSize', 'f32'],
      ['uBokehBright', 'f32'],
      ['uRings', 'i32'],
      ['uHasDepth', 'i32'],
      ['uFrame', 'f32'],
    ],
  },

  composite: {
    label: 'composite',
    fragment: FRAG_COMPOSITE,
    textures: ['texScene', 'texBloom', 'texOriginal', 'texGodRays', 'texAIMask', 'texVolumetric'],
    uniforms: [
      ['uResolution', 'vec2f'],
      ['uColorTint', 'vec3f'],
      ['uEdgeColor', 'vec3f'],
      ['uAIMaskColor', 'vec3f'],
      ['uTime', 'f32'],
      ['uExposure', 'f32'],
      ['uGamma', 'f32'],
      ['uBloomIntensity', 'f32'],
      ['uGodRaysIntensity', 'f32'],
      ['uVolumetricIntensity', 'f32'],
      ['uSharpen', 'f32'],
      ['uVignetteIntensity', 'f32'],
      ['uVignetteRadius', 'f32'],
      ['uSaturation', 'f32'],
      ['uContrast', 'f32'],
      ['uColorTemp', 'f32'],
      ['uDofIntensity', 'f32'],
      ['uDofFocus', 'f32'],
      ['uDofRange', 'f32'],
      ['uEdgeIntensity', 'f32'],
      ['uChromAbIntensity', 'f32'],
      ['uGrainIntensity', 'f32'],
      ['uSplitPos', 'f32'],
      ['uAIMaskOpacity', 'f32'],
      ['uToneMapMode', 'i32'],
      ['uInputIsLinear', 'i32'],
      ['uHasVolumetric', 'i32'],
      ['uFrame', 'f32'],
      ['uMidGray', 'f32'],
    ],
  },

  fxaa: {
    label: 'fxaa',
    fragment: FRAG_FXAA,
    textures: ['texSrc'],
    uniforms: [
      ['uResolution', 'vec2f'],
      ['uSpanMax', 'f32'],
      ['uReduceMul', 'f32'],
    ],
  },

  blit: {
    label: 'blit',
    fragment: FRAG_BLIT,
    textures: ['texSrc'],
    uniforms: [
      ['uResolution', 'vec2f'],
      ['uOpacity', 'f32'],
      ['uUseNearest', 'i32'],
    ],
  },
});

/**
 * Собрать полный исходник шейдерного модуля прохода.
 * Пролог + вершинный шейдер + фрагментный — в одном GPUShaderModule,
 * чтобы не плодить модули и переиспользовать вершинную часть.
 */
export function buildShaderSource(passName) {
  const pass = RENDER_PASSES[passName];
  if (!pass) throw new Error(`[WGSL] Unknown render pass: ${passName}`);
  return `${WGSL_COMMON}\n${WGSL_VERTEX}\n${pass.fragment}`;
}

export const RENDER_PASS_NAMES = Object.freeze(Object.keys(RENDER_PASSES));
